import type { AiVisibilityDatasetResponse } from '@cmoh/shared'
import { CalendarX2 } from 'lucide-react'
import { Suspense, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useApiQuery } from '@/hooks/use-api'
import { useCurrentOrganization } from '@/hooks/use-current-organization'
import {
  AI_VISIBILITY_API_PATHS,
  AI_VISIBILITY_QUERY_KEYS,
  DATASET_SELECTION_MODE,
  type DatasetSelection,
  datasetSelectionKey,
  datasetSelectionParams,
} from '../api/ai-visibility.api'
import { useAiVisibilityRuns } from '../hooks/use-ai-visibility-runs'
import {
  DataDatasetContext,
  DataRangeSelectionContext,
  DataRunInfoContext,
  type DataDateRange,
  type DataRangeSelectionValue,
} from './dataset-context'
import { DataModuleSkeleton } from './DataModuleSkeleton'
import { RunRangePicker } from './RunRangePicker'
import type { DataDataset } from './types'

const TABLE_SECTION_PATTERN = /\/(prompts|responses|sources|actions)(\/|$)/

const FROM_PARAM = 'from'
const TO_PARAM = 'to'

export function DataDatasetProvider({
  children,
}: {
  children: ReactElement
}): ReactElement {
  const { organization, isLoading: isOrganizationLoading } = useCurrentOrganization()
  const organizationId = organization?.id ?? null
  const moduleEnabled = organizationId !== null && organization?.aiVisibility != null
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // The selected range lives in provider state so it survives navigation
  // between the module's sections (the provider is the layout for /data/*).
  const [range, setRange] = useState<DataDateRange | null>(() => {
    const from = searchParams.get(FROM_PARAM)
    const to = searchParams.get(TO_PARAM)
    return from && to ? { from, to } : null
  })

  const lastUrlRangeRef = useRef<string | null>(null)

  useEffect(() => {
    const from = searchParams.get(FROM_PARAM)
    const to = searchParams.get(TO_PARAM)
    const urlRange = from !== null && to !== null ? `${from}|${to}` : null
    const urlRangeChanged = urlRange !== lastUrlRangeRef.current
    lastUrlRangeRef.current = urlRange

    if (urlRangeChanged && from !== null && to !== null) {
      if (range === null || from !== range.from || to !== range.to) {
        setRange({ from, to })
      }
      return
    }

    const urlMatchesRange =
      (range === null && urlRange === null) ||
      (range !== null && from === range.from && to === range.to)
    if (urlMatchesRange) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (range) {
          next.set(FROM_PARAM, range.from)
          next.set(TO_PARAM, range.to)
        } else {
          next.delete(FROM_PARAM)
          next.delete(TO_PARAM)
        }
        return next
      },
      { replace: true },
    )
  }, [range, searchParams, setSearchParams])

  const runsQuery = useAiVisibilityRuns()
  const runs = runsQuery.data ?? null

  const runsInRange = useMemo(() => {
    if (!runs || !range) return null
    return runs.filter((run) => run.exportDate >= range.from && run.exportDate <= range.to)
  }, [runs, range])

  const isEmptyRange = range !== null && runsInRange !== null && runsInRange.length === 0

  const selection = useMemo<DatasetSelection | null>(() => {
    if (!runs || runs.length === 0) return null
    if (!range) {
      return { mode: DATASET_SELECTION_MODE.RUN, runId: runs[0].runId }
    }
    if (!runsInRange || runsInRange.length === 0) {
      return null
    }
    if (runsInRange.length === 1) {
      return { mode: DATASET_SELECTION_MODE.RUN, runId: runsInRange[0].runId }
    }
    return { mode: DATASET_SELECTION_MODE.RANGE, from: range.from, to: range.to }
  }, [runs, range, runsInRange])

  const selectionKey = selection ? datasetSelectionKey(selection) : null

  const datasetQuery = useApiQuery<AiVisibilityDatasetResponse>(
    AI_VISIBILITY_QUERY_KEYS.dataset(organizationId, selectionKey),
    AI_VISIBILITY_API_PATHS.DATASET,
    selection !== null ? { params: datasetSelectionParams(selection) } : undefined,
    {
      enabled: moduleEnabled && selection !== null,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      retry: 1,
    },
  )
  const { data } = datasetQuery

  const dataset = useMemo<DataDataset | null>(() => {
    if (!data) return null
    return { ...data.dataset, ...data.meta } as DataDataset
  }, [data])

  const rangeSelection = useMemo<DataRangeSelectionValue>(
    () => ({ range, setRange }),
    [range],
  )

  const { pathname } = useLocation()
  const skeletonVariant = TABLE_SECTION_PATTERN.test(pathname) ? 'table' : 'dashboard'

  const isLoading =
    isOrganizationLoading ||
    (moduleEnabled && (runsQuery.isPending || (selection !== null && datasetQuery.isPending)))

  if (isLoading) {
    return <DataModuleSkeleton variant={skeletonVariant} />
  }

  if (moduleEnabled && isEmptyRange) {
    return (
      <DataRangeSelectionContext.Provider value={rangeSelection}>
        <div className="flex min-h-screen flex-col gap-6 bg-dashboard px-4 py-6 md:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <RunRangePicker />
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-center">
            <CalendarX2 className="size-8 text-neutral-400" />
            <p className="text-sm font-medium text-neutral-700">{t('data.rangeEmpty.title')}</p>
            <p className="text-sm text-neutral-500">{t('data.rangeEmpty.hint')}</p>
            <button
              type="button"
              className="mt-1 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
              onClick={() => setRange(null)}
            >
              {t('data.rangeEmpty.reset')}
            </button>
          </div>
        </div>
      </DataRangeSelectionContext.Provider>
    )
  }

  if (!moduleEnabled || runsQuery.isError || datasetQuery.isError || !data || !dataset) {
    return (
      <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 text-neutral-700">
        <p className="text-sm">{t('data.loadError')}</p>
        {datasetQuery.error instanceof Error ? (
          <p className="text-xs text-neutral-500">{datasetQuery.error.message}</p>
        ) : null}
        <button
          type="button"
          className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm hover:bg-neutral-50"
          onClick={() => {
            runsQuery.refetch()
            datasetQuery.refetch()
          }}
        >
          {t('common.retry')}
        </button>
      </div>
    )
  }

  return (
    <DataRangeSelectionContext.Provider value={rangeSelection}>
      <DataRunInfoContext.Provider value={data.run}>
        <DataDatasetContext.Provider value={dataset}>
          <Suspense fallback={<DataModuleSkeleton variant={skeletonVariant} />}>{children}</Suspense>
        </DataDatasetContext.Provider>
      </DataRunInfoContext.Provider>
    </DataRangeSelectionContext.Provider>
  )
}
